'use client';

import { useEffect, useId, useState } from 'react';
import { Instagram } from 'lucide-react';
import { instagramPermalink } from '@/lib/instagram-media-url';

type Quote = { id: string; channel: 'instagram'; text: string; authorUsername: string; sourcePermalink: string; postedAt: string | null };
type Props = { channel: 'site' | 'marketplace'; handle: string; compact?: boolean; className?: string };
const bytes = (value: unknown, max: number): value is string => typeof value === 'string' && !!value.trim() && new TextEncoder().encode(value).length <= max;
function quotes(body: unknown): Quote[] {
  if (!body || typeof body !== 'object' || !('comments' in body) || !Array.isArray(body.comments) || body.comments.length > 24) return [];
  const seen = new Set<string>();
  return body.comments.flatMap((row: unknown) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return [];
    const q = row as Quote;
    if (typeof q.id !== 'string' || !/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(q.id) || seen.has(q.id)
      || q.channel !== 'instagram' || !bytes(q.text, 16384) || !bytes(q.authorUsername, 256)
      || !q.sourcePermalink || instagramPermalink(q.sourcePermalink) !== q.sourcePermalink
      || q.postedAt !== null && (typeof q.postedAt !== 'string' || !Number.isFinite(Date.parse(q.postedAt)))) return [];
    seen.add(q.id); return [{ id: q.id, channel: q.channel, text: q.text, authorUsername: q.authorUsername, sourcePermalink: q.sourcePermalink, postedAt: q.postedAt }];
  });
}
function Comment({ quote }: { quote: Quote }) {
  const [expanded, setExpanded] = useState(false), id = useId(), collapsible = quote.text.length > 180 || quote.text.split('\n').length > 4;
  return <li className="min-w-0 rounded-xl border bg-card p-4 text-card-foreground">
    <figure className="space-y-2">
      <blockquote id={id} className={`whitespace-pre-line break-words text-sm ${collapsible && !expanded ? 'line-clamp-4' : ''}`}>{quote.text}</blockquote>
      {collapsible && <button type="button" className="inline-flex min-h-11 items-center text-sm underline"
        aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(value => !value)}>{expanded ? 'Show less' : 'Read full comment'}</button>}
      <figcaption className="space-y-1 text-sm text-muted-foreground">
        <p className="break-words">@{quote.authorUsername.replace(/^@/, '')}</p>
        {quote.postedAt && <time className="block text-xs" dateTime={quote.postedAt}>{new Date(quote.postedAt).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</time>}
        <a href={quote.sourcePermalink} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-1 underline"
          aria-label={`View Instagram source post for @${quote.authorUsername.replace(/^@/, '')} (opens in a new tab)`}>View on Instagram</a>
      </figcaption>
    </figure>
  </li>;
}
function CommentRequest({ path, compact, className }: { path: string; compact?: boolean; className?: string }) {
  const [items, setItems] = useState<Quote[]>([]), heading = useId();
  useEffect(() => {
    let active: AbortController | null = null, disposed = false;
    const refresh = async () => {
      if (disposed || active || document.visibilityState === 'hidden') return;
      const controller = new AbortController(); active = controller;
      const timeout = setTimeout(() => { if (!disposed && active === controller) setItems([]); controller.abort(); }, 15000);
      try {
        const response = await fetch(path, { credentials: 'omit', cache: 'no-store', redirect: 'error', signal: controller.signal });
        const parsed = response.ok ? quotes(await response.json()) : [];
        if (!disposed && !controller.signal.aborted) setItems(parsed);
      } catch { if (!disposed && active === controller) setItems([]); }
      finally { clearTimeout(timeout); if (active === controller) active = null; }
    };
    const visibility = () => {
      active?.abort(); active = null; setItems([]);
      if (document.visibilityState !== 'hidden') void refresh();
    };
    void refresh(); const timer = setInterval(() => { void refresh(); }, 60000);
    document.addEventListener('visibilitychange', visibility);
    return () => { disposed = true; clearInterval(timer); active?.abort(); document.removeEventListener('visibilitychange', visibility); };
  }, [path]);
  if (!items.length) return null;
  return <section className={className} aria-labelledby={heading}>
    <h2 id={heading} className="flex items-center gap-2 text-lg font-semibold"><Instagram className="h-5 w-5" aria-hidden="true" />Comments from Instagram</h2>
    <p className="mt-1 text-sm text-muted-foreground">Comments on this shop’s Instagram posts.</p>
    <ul className={`mt-4 grid gap-3 ${compact ? '' : 'sm:grid-cols-2'}`}>{items.map(quote => <Comment key={`${quote.id}:${quote.text}`} quote={quote} />)}</ul>
  </section>;
}
export function PublicInstagramComments({ channel, handle, ...props }: Props) {
  if (!handle || !['site', 'marketplace'].includes(channel)) return null;
  const path = `/api/proxy/storefront/${channel === 'site' ? 'site/' : ''}${encodeURIComponent(handle)}/instagram/comments`;
  return <CommentRequest key={path} path={path} {...props} />;
}
